import type { Account } from "@aptos-labs/ts-sdk";

import { TERMS_ACCEPTED_KEY } from "@constants";
import { wait } from "@utils/wait";

import { aptosNetwork } from "@src/api/aptos";
import { storageService } from "../storage";

import { subAccountService } from "./SubAccountService";
import { createAccountFromPrivateKey } from "./helpers";
import type { SubAccount, Wallet } from "./types";

export interface ConnectionFlowState {
  isFullyConnected: boolean;
  isPartiallyConnected: boolean;
  isLoadingSubAccount: boolean;
  subAccountsLoaded: boolean;
  wallet: Wallet | null;
  subAccount: Account | null;
}

export interface ConnectionFlowCallbacks {
  setSubAccount: (account: Account | null) => void;
  setActiveTradingAddress: (address: string) => void;
  getAllSubAccounts: () => Map<string, SubAccount[]>;
  setAllSubAccounts: (address: string, accounts: SubAccount[]) => void;
  generateRootAccountToken: () => Promise<void>;
  openTermsDialog: () => void;
  closeTermsDialog: () => void;
  openSelectWalletDialog: () => void;
  closeSelectWalletDialog: () => void;
  openStartTradingDialog: () => void;
  closeAllDialogs: () => void;
  showToast: (toast: { type: "error" | "info" | "success" | "warning"; title: string; content: string }) => void;
  reset: () => void;
}

export class ConnectionFlowService {
  createSubAccount = async (
    wallet: Wallet,
    existingAccounts: SubAccount[],
    callbacks: Pick<
      ConnectionFlowCallbacks,
      "setSubAccount" | "setActiveTradingAddress" | "setAllSubAccounts" | "showToast"
    >,
  ): Promise<{ fundingSubAccount: SubAccount; tradingSubAccount: SubAccount } | null> => {
    if (!wallet.account) return null;

    const address = wallet.account.address.toString();

    try {
      const result = await subAccountService.signAccountsWithMasterSignature(wallet);

      if (!result) {
        throw new Error("Failed to sign accounts");
      }

      const { funding: fundingSubAccount, trading: tradingSubAccount } = result;

      const accountsToSave = [...existingAccounts, fundingSubAccount, tradingSubAccount];
      callbacks.setAllSubAccounts(address, accountsToSave);
      await storageService.setSubAccounts(new Map([[address, accountsToSave]]));

      const fundingAccount = createAccountFromPrivateKey(fundingSubAccount.privateKey);
      callbacks.setSubAccount(fundingAccount);
      callbacks.setActiveTradingAddress(tradingSubAccount.address);

      return { fundingSubAccount, tradingSubAccount };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      const isUserRejected =
        errorMessage.toLowerCase().includes("reject") || errorMessage.toLowerCase().includes("denied");

      callbacks.showToast({
        type: "error" as const,
        title: isUserRejected ? "Signature Required" : "Account Creation Failed",
        content: isUserRejected
          ? "Please sign the message to create your accounts"
          : `Failed to create sub accounts: ${errorMessage}`,
      });

      throw error;
    }
  };

  shouldShowStartTradingDialog = async (wallet: Wallet | null, forceRefresh = false): Promise<boolean> => {
    if (!wallet?.account) return false;

    const walletNetwork = wallet.network?.name?.toLowerCase();
    const expectedNetwork = aptosNetwork.toLowerCase();
    // After a full page reload, many adapters report `connected` (and we restore subAccount)
    // before `network` is hydrated. Treating missing network as "wrong chain" always returned
    // true here and reopened the onboarding dialog incorrectly.
    const networkIsKnown = Boolean(walletNetwork);
    const isWrongNetwork = networkIsKnown && walletNetwork !== expectedNetwork;

    const isLinked = await subAccountService.checkAccountsLinkedToBlockchain(
      wallet.account.address.toString(),
      forceRefresh,
    );
    return isWrongNetwork || !isLinked;
  };

  disconnect = async (
    wallet: Wallet | null,
    callbacks: Pick<ConnectionFlowCallbacks, "reset" | "closeAllDialogs">,
    preserveData?: { vaultContractKey?: string },
  ): Promise<void> => {
    try {
      if (wallet?.connected) {
        await wallet.disconnect();
      }
    } catch {
      // Silent
    }

    callbacks.reset();
    await storageService.clear(preserveData);
    callbacks.closeAllDialogs();
    await wait(100);
  };

  initializeSubAccountFromStorage = (
    storedAccounts: SubAccount[],
    callbacks: Pick<ConnectionFlowCallbacks, "setSubAccount">,
  ): void => {
    const fundingAccount = storedAccounts.find((acc) => acc.type === "funding" && acc.privateKey);

    if (fundingAccount?.privateKey) {
      const account = createAccountFromPrivateKey(fundingAccount.privateKey);
      callbacks.setSubAccount(account);
    }
  };

  selectInitialTradingAccount = (storedAccounts: SubAccount[], savedAddress: string | null): SubAccount | null => {
    const tradingAccountsWithKeys = storedAccounts.filter((acc) => acc.type === "trading" && acc.privateKey);

    if (tradingAccountsWithKeys.length === 0) return null;

    if (savedAddress) {
      const saved = tradingAccountsWithKeys.find((acc) => acc.address === savedAddress);
      if (saved) return saved;
    }

    return tradingAccountsWithKeys.find((acc) => acc.nonce === "0") || tradingAccountsWithKeys[0];
  };

  hasAcceptedTerms = (): boolean => {
    return localStorage.getItem(TERMS_ACCEPTED_KEY) === "true";
  };
}

export const connectionFlowService = new ConnectionFlowService();
